/**
 * A field that had to hold an Enter back — it waited for the server to resolve
 * what was typed (`NxAutocompleteInput`'s `resolveCode`) — dispatches this
 * bubbling event from itself once the value is committed. A form that moves on
 * with Enter (the posting mask, BLP/nx#3198) then goes on as if the Enter had
 * passed.
 */
export const NX_ENTER_COMMITTED_EVENT = 'nx-enter-committed';

/**
 * The Tab twin of `NX_ENTER_COMMITTED_EVENT`: a field that took a Tab over to
 * resolve what was typed (`NxAutocompleteInput`'s `resolveCode`) dispatches
 * this bubbling, cancelable event from itself before it moves the focus on. A
 * form that moves the cursor itself once the field is settled (the posting
 * mask, BLP/nx#3198) cancels it; uncancelled, the field focuses the next tab
 * stop at once, as before.
 */
export const NX_TAB_COMMITTED_EVENT = 'nx-tab-committed';

/**
 * Whether a key press is a plain Enter a form may take for itself — moving on
 * to the next field, taking over a row. It is not when the control
 * uses Enter itself: a field whose list is open (`aria-expanded="true"`), a
 * text area or a rich-text editor (a new line), a button or a link (its click);
 * nor when the control already handled it (`defaultPrevented`), an input method
 * is composing, or a modifier is held. Shift is the caller's to decide
 * (`allowShift`): moving back with Shift+Enter is the usual pairing.
 */
export function isNxPlainEnter(
  event: KeyboardEvent,
  options: { allowShift?: boolean } = {}
): boolean {
  if (event.key !== 'Enter' || event.defaultPrevented || event.isComposing) return false;
  if (event.altKey || event.ctrlKey || event.metaKey) return false;
  if (event.shiftKey && !options.allowShift) return false;
  const target = event.target;
  if (typeof Element === 'undefined' || !(target instanceof Element)) return false;
  if (target instanceof HTMLTextAreaElement) return false;
  if (target.closest('button, a[href], [role="button"], [role="link"]')) return false;
  const editable = target.closest('[contenteditable]');
  if (editable && editable.getAttribute('contenteditable') !== 'false') return false;
  return !target.closest('[aria-expanded="true"]');
}
